import React, { useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  ReferenceLine,
} from "recharts";
import { getDetailedClimateColor } from "../utils/climateClassification";
import koppenDescriptions from "../data/koppen-descriptions.json";
import {
  OPENROUTER_API_KEY,
  API_BASE_URL,
  isConfigValid,
} from "../utils/config";

const CloudCoverageAnalysis = ({
  climateInfo,
  cloudCoverData,
  solarRadiationData,
}) => {
  const [aiInterpretation, setAiInterpretation] = useState(null);
  const [loadingAiInterpretation, setLoadingAiInterpretation] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [designSuggestions, setDesignSuggestions] = useState(null);
  const [loadingDesignSuggestions, setLoadingDesignSuggestions] =
    useState(false);
  const [designError, setDesignError] = useState(null);

  if (!climateInfo || !cloudCoverData) {
    return null;
  }

  // Calculate cloud coverage performance metrics
  const annualAvgCloudCover = cloudCoverData.data
    ? cloudCoverData.data.reduce((sum, d) => sum + d.mean, 0) /
      cloudCoverData.data.length
    : 0;

  const maxCloudCover = cloudCoverData.data
    ? Math.max(...cloudCoverData.data.map((d) => d.max || d.mean))
    : 0;

  const minCloudCover = cloudCoverData.data
    ? Math.min(...cloudCoverData.data.map((d) => d.min || d.mean))
    : 0;

  const cloudRange = maxCloudCover - minCloudCover;

  // Calculate solar potential (inverse of cloud coverage)
  const solarPotential = 100 - annualAvgCloudCover;

  // Calculate clear sky days (less than 30% cloud coverage is considered clear)
  const clearDays = cloudCoverData.data
    ? cloudCoverData.data.filter((d) => d.mean <= 30).length
    : 0;

  const clearDaysPercentage = cloudCoverData.data
    ? (clearDays / cloudCoverData.data.length) * 100
    : 0;

  // Calculate average solar radiation if available
  const avgSolarRadiation = solarRadiationData?.data
    ? solarRadiationData.data.reduce(
        (sum, d) => sum + (d.mean || d.global_horizontal_rad || 0),
        0
      ) / solarRadiationData.data.length
    : 4500 + parseFloat(climateInfo.annualTemp || 0) * 10; // Fallback estimation

  return (
    <div
      id="cloud-coverage-analysis-section"
      className="min-h-screen bg-gray-100 px-14 py-8 pt-28"
    >
      {/* Cloud Coverage Heading */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 tracking-wide">
          Cloud Coverage & Solar
        </h1>
      </div>

      {/* Three Cards Layout - Top Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* MACRO Card */}
        <div className="bg-gray-100 border border-gray-500 p-8 rounded-[2rem]">
          <div className="border-b border-gray-300 pb-2 mb-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              MACRO
            </h3>
          </div>

          {/* Top section - cloud coverage metrics */}
          <div className="flex justify-between items-start mb-8">
            {/* Left side - Max and Min cloud coverage */}
            <div className="flex-1">
              <div className="mb-4">
                <div className="text-sm text-gray-500 font-bold mb-1">
                  Maximum Coverage
                </div>
                <div className="text-2xl font-black text-gray-900">
                  {maxCloudCover.toFixed(1)}%
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500 font-bold mb-1">
                  Minimum Coverage
                </div>
                <div className="text-2xl font-black text-gray-900">
                  {minCloudCover.toFixed(1)}%
                </div>
              </div>
            </div>

            {/* Right side - Annual average cloud coverage */}
            <div className="flex-1 text-right">
              <div className="text-5xl font-black text-black mb-2">
                {annualAvgCloudCover.toFixed(1)}%
              </div>
              <div className="text-sm text-gray-500 font-bold">
                Annual Avg. Cloud Cover
              </div>
            </div>
          </div>

          {/* Bottom section - Classification information */}
          <div>
            {/* Large Köppen classification code */}
            <div
              className="text-6xl font-black leading-none mb-4"
              style={{
                color: getDetailedClimateColor(
                  climateInfo.mainClass,
                  climateInfo.subClass
                ),
              }}
            >
              {climateInfo.mainClass}
              {climateInfo.subClass}
            </div>

            {/* Classification label */}
            <div className="text-sm text-gray-500 font-bold mb-4">
              Köppen-Geiger Climate Classification
            </div>

            {/* Climate name */}
            <div className="font-bold text-gray-800 mb-4 text-lg">
              {climateInfo.fullName}
            </div>

            {/* Description */}
            <div className="text-sm text-gray-500 leading-relaxed font-bold">
              <div>
                {annualAvgCloudCover >= 80
                  ? "Predominantly overcast conditions with limited direct solar access throughout the year."
                  : annualAvgCloudCover >= 60
                  ? "Frequently cloudy skies with moderate solar potential and diffused lighting conditions."
                  : annualAvgCloudCover >= 40
                  ? "Partly cloudy conditions offering balanced solar access and natural lighting opportunities."
                  : "Clear skies dominate with excellent solar potential and abundant direct sunlight for passive design strategies."}
              </div>
            </div>
          </div>
        </div>

        {/* MICRO Card */}
        <div className="bg-gray-100 border border-gray-500 p-8 rounded-[2rem] flex flex-col h-full">
          <div className="border-b border-gray-300 pb-2 mb-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              MICRO
            </h3>
          </div>

          {/* Solar Potential at top */}
          <div className="text-left mb-8">
            <div className="text-5xl font-black text-black mb-2">
              {solarPotential.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-500 font-bold">
              Solar Potential
            </div>
          </div>

          {/* Seasonal Cloud Patterns */}
          <div className="mb-8 flex-grow">
            <h4 className="font-bold text-gray-500 mb-4 text-lg">
              Seasonal Patterns
            </h4>

            <div className="grid grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4 text-sm">
                <div>
                  <div className="font-bold text-gray-800 mb-1">Winter:</div>
                  <div className="font-bold text-gray-500">
                    Avg:{" "}
                    {cloudCoverData.data
                      ? (() => {
                          const winterData = cloudCoverData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (cloudCoverData.data.length / 12)
                                  ) + 1;
                              return month === 12 || month === 1 || month === 2;
                            }
                          );
                          return winterData.length > 0
                            ? (
                                winterData.reduce((sum, d) => sum + d.mean, 0) /
                                winterData.length
                              ).toFixed(1)
                            : "--";
                        })()
                      : "--"}
                    %
                  </div>
                </div>

                <div>
                  <div className="font-bold text-gray-800 mb-1">Spring:</div>
                  <div className="font-bold text-gray-500">
                    Avg:{" "}
                    {cloudCoverData.data
                      ? (() => {
                          const springData = cloudCoverData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (cloudCoverData.data.length / 12)
                                  ) + 1;
                              return month === 3 || month === 4 || month === 5;
                            }
                          );
                          return springData.length > 0
                            ? (
                                springData.reduce((sum, d) => sum + d.mean, 0) /
                                springData.length
                              ).toFixed(1)
                            : "--";
                        })()
                      : "--"}
                    %
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4 text-sm">
                <div>
                  <div className="font-bold text-gray-800 mb-1">Summer:</div>
                  <div className="font-bold text-gray-500">
                    Avg:{" "}
                    {cloudCoverData.data
                      ? (() => {
                          const summerData = cloudCoverData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (cloudCoverData.data.length / 12)
                                  ) + 1;
                              return month === 6 || month === 7 || month === 8;
                            }
                          );
                          return summerData.length > 0
                            ? (
                                summerData.reduce((sum, d) => sum + d.mean, 0) /
                                summerData.length
                              ).toFixed(1)
                            : "--";
                        })()
                      : "--"}
                    %
                  </div>
                </div>

                <div>
                  <div className="font-bold text-gray-800 mb-1">Fall:</div>
                  <div className="font-bold text-gray-500">
                    Avg:{" "}
                    {cloudCoverData.data
                      ? (() => {
                          const fallData = cloudCoverData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (cloudCoverData.data.length / 12)
                                  ) + 1;
                              return (
                                month === 9 || month === 10 || month === 11
                              );
                            }
                          );
                          return fallData.length > 0
                            ? (
                                fallData.reduce((sum, d) => sum + d.mean, 0) /
                                fallData.length
                              ).toFixed(1)
                            : "--";
                        })()
                      : "--"}
                    %
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Solar Radiation */}
          <div className="flex justify-between items-center mt-auto">
            <div className="text-left">
              <div className="text-4xl font-black text-black">
                {minCloudCover.toFixed(1)}%
              </div>
              <div className="text-sm text-gray-500 font-bold">
                clearest conditions
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-black text-black">
                {Math.round(avgSolarRadiation)}
              </div>
              <div className="text-sm text-gray-500 font-bold">
                Wh/m² daily avg
              </div>
            </div>
          </div>
        </div>

        {/* DESIGN Card */}
        <div className="bg-gray-100 border border-gray-500 p-8 rounded-[2rem] flex flex-col h-full">
          <div className="border-b border-gray-300 pb-2 mb-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              DESIGN
            </h3>
          </div>

          <div className="flex space-x-4 mb-4">
            <div className="w-20 h-14 bg-gray-800 rounded flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-white rounded-sm"></div>
            </div>
            <div className="w-20 h-14 bg-gray-800 rounded flex items-center justify-center">
              <div className="w-8 h-8 bg-white transform rotate-45"></div>
            </div>
          </div>

          <div className="mb-4 flex-grow">
            <h4 className="font-bold text-gray-800 mb-2">
              Climate Interpretation & Design Implications
            </h4>

            {/* Conditional content: Show loading, AI suggestions, error, or static text */}
            {loadingDesignSuggestions ? (
              <div className="text-sm text-gray-700 space-y-2">
                <style jsx>{`
                  @keyframes cloudWave1 {
                    0%,
                    100% {
                      background-color: #d1d5db;
                    }
                    50% {
                      background-color: #9ca3af;
                    }
                  }
                  @keyframes cloudWave2 {
                    0%,
                    100% {
                      background-color: #9ca3af;
                    }
                    50% {
                      background-color: #d1d5db;
                    }
                  }
                  @keyframes cloudWave3 {
                    0%,
                    100% {
                      background-color: #e5e7eb;
                    }
                    50% {
                      background-color: #9ca3af;
                    }
                  }
                  .animate-cloud-wave-1 {
                    animation: cloudWave1 2s ease-in-out infinite;
                  }
                  .animate-cloud-wave-2 {
                    animation: cloudWave2 2s ease-in-out infinite 0.3s;
                  }
                  .animate-cloud-wave-3 {
                    animation: cloudWave3 2s ease-in-out infinite 0.6s;
                  }
                `}</style>
                <div className="h-3 rounded animate-cloud-wave-1"></div>
                <div className="h-3 rounded animate-cloud-wave-2 w-4/5"></div>
                <div className="h-3 rounded animate-cloud-wave-3 w-3/5"></div>
                <div className="h-3 rounded animate-cloud-wave-1 w-5/6"></div>
                <div className="h-3 rounded animate-cloud-wave-2 w-2/3"></div>
                <div className="h-3 rounded animate-cloud-wave-3"></div>
              </div>
            ) : designSuggestions ? (
              <div className="text-sm text-gray-500 leading-relaxed font-bold whitespace-pre-wrap">
                {designSuggestions}
              </div>
            ) : designError ? (
              <div className="text-red-600 text-sm font-bold">
                Error: {designError}
              </div>
            ) : (
              <div className="text-sm text-gray-700 space-y-2">
                <p>
                  This location has{" "}
                  {annualAvgCloudCover >= 80
                    ? "limited solar access requiring enhanced daylighting strategies and efficient artificial lighting systems"
                    : annualAvgCloudCover >= 60
                    ? "moderate solar potential benefiting from strategic window placement and solar heat gain optimization"
                    : annualAvgCloudCover >= 40
                    ? "good solar access enabling effective passive solar design and natural daylighting strategies"
                    : "excellent solar potential ideal for passive solar heating, natural lighting, and renewable energy systems"}{" "}
                  with {annualAvgCloudCover.toFixed(1)}% average cloud coverage.
                </p>
                <p>
                  The {cloudRange.toFixed(1)}% seasonal cloud variation{" "}
                  {cloudRange > 40
                    ? "requires adaptive shading and variable daylighting controls to manage seasonal solar conditions"
                    : "allows for consistent solar design strategies throughout the year"}{" "}
                  with {clearDaysPercentage.toFixed(1)}% clear sky days and{" "}
                  {Math.round(avgSolarRadiation)} Wh/m² daily solar radiation.
                </p>
              </div>
            )}

            {/* Design Suggestion Text and AI Button */}
            <div className="flex items-center gap-3 mt-6">
              <span className="text-black text-sm font-bold">
                Design Suggestion
              </span>
              <button
                className="bg-gray-600 hover:bg-[#206ab2] text-white px-1.5 py-1 rounded text-xs font-bold transition-colors"
                onClick={async () => {
                  if (!OPENROUTER_API_KEY) {
                    setDesignError("OpenRouter API key not configured");
                    return;
                  }

                  setDesignSuggestions(null);
                  setDesignError(null);
                  setLoadingDesignSuggestions(true);

                  try {
                    const response = await fetch(
                      "https://openrouter.ai/api/v1/chat/completions",
                      {
                        method: "POST",
                        headers: {
                          Authorization: `Bearer ${OPENROUTER_API_KEY}`,
                          "HTTP-Referer": window.location.origin,
                          "X-Title": "ClimaPro.AI",
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          model: "anthropic/claude-3.7-sonnet",
                          route: "fallback",
                          messages: [
                            {
                              role: "system",
                              content:
                                "You are an expert building design consultant specializing in solar design, daylighting, and cloud coverage analysis in architecture. Analyze the cloud coverage and solar metrics provided to give location-specific building design recommendations. Structure your response as exactly 3 paragraphs: 1) Solar access strategy, 2) Daylighting approach, 3) Shading and energy priorities. Include specific cloud coverage and solar numbers in your reasoning. Keep total response under 75 words with actionable, solar-focused design advice.",
                            },
                            {
                              role: "user",
                              content: `For this ${climateInfo.mainClass}${
                                climateInfo.subClass
                              } (${
                                climateInfo.fullName
                              }) climate location, provide targeted building design recommendations based on these solar metrics:

SOLAR & CLOUD PROFILE:
- Annual Average Cloud Coverage: ${annualAvgCloudCover.toFixed(1)}%
- Solar Potential: ${solarPotential.toFixed(1)}%
- Maximum Cloud Coverage: ${maxCloudCover.toFixed(1)}%
- Minimum Cloud Coverage: ${minCloudCover.toFixed(1)}%
- Cloud Range: ${cloudRange.toFixed(1)}%
- Clear Sky Days: ${clearDaysPercentage.toFixed(1)}%
- Daily Solar Radiation: ${Math.round(avgSolarRadiation)} Wh/m²

Based on this solar and cloud data, provide design recommendations with clear reasoning:

1. SOLAR ACCESS: Explain passive solar and orientation strategies based on the ${solarPotential.toFixed(
                                1
                              )}% solar potential and ${Math.round(
                                avgSolarRadiation
                              )} Wh/m² radiation. Reference these numbers in your reasoning.

2. DAYLIGHTING: Justify window sizing and placement based on the ${annualAvgCloudCover.toFixed(
                                1
                              )}% cloud coverage and ${clearDaysPercentage.toFixed(
                                1
                              )}% clear days. Use these specific values in your explanation.

3. SHADING & ENERGY: Recommend shading strategies and energy systems based on the ${cloudRange.toFixed(
                                1
                              )}% seasonal cloud variation. Explain why these specific solar numbers drive your recommendations.

Use the actual solar values (${solarPotential.toFixed(
                                1
                              )}% potential, ${annualAvgCloudCover.toFixed(
                                1
                              )}% clouds, ${Math.round(
                                avgSolarRadiation
                              )} Wh/m²) to justify each design decision.`,
                            },
                          ],
                        }),
                      }
                    );

                    if (!response.ok) {
                      if (response.status === 503) {
                        throw new Error("AI service temporarily unavailable");
                      } else if (response.status === 401) {
                        throw new Error("Authentication failed");
                      } else if (response.status === 429) {
                        throw new Error("Rate limit exceeded");
                      }
                      throw new Error(`Request failed: ${response.status}`);
                    }

                    const data = await response.json();

                    if (
                      data.choices &&
                      data.choices[0] &&
                      data.choices[0].message
                    ) {
                      setDesignSuggestions(data.choices[0].message.content);
                    } else if (data.error) {
                      throw new Error(data.error.message || "Unknown AI error");
                    }
                  } catch (error) {
                    console.error(
                      "AI cloud coverage suggestions error:",
                      error
                    );
                    setDesignError(error.message);
                  } finally {
                    setLoadingDesignSuggestions(false);
                  }
                }}
                disabled={loadingDesignSuggestions}
              >
                {loadingDesignSuggestions ? "..." : "ClimaPro.AI"}
              </button>
            </div>
          </div>

          <div className="flex items-end justify-between mt-auto">
            <div>
              <div className="text-sm text-gray-600 mb-1">+ SOLAR ACCESS</div>
              <div className="text-sm text-gray-600">+ DAYLIGHTING</div>
            </div>
            <div className="text-right">
              <div className="text-5xl font-bold text-gray-900">
                {clearDaysPercentage.toFixed(1)}%
              </div>
              <div className="text-sm text-gray-500 font-bold">
                Clear Sky Days
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cloud Coverage Graph Card - Bottom Section */}
      <div className="bg-gray-100 border border-gray-500 rounded-[2rem] overflow-hidden">
        <button
          className="w-full p-4 flex justify-between items-center text-left hover:bg-gray-200 transition-colors"
          onClick={() => {
            const graphContent = document.getElementById("cloud-graph-content");
            const chevron = document.getElementById("cloud-graph-chevron");
            if (
              graphContent.style.display === "none" ||
              !graphContent.style.display
            ) {
              graphContent.style.display = "block";
              chevron.style.transform = "rotate(180deg)";
            } else {
              graphContent.style.display = "none";
              chevron.style.transform = "rotate(0deg)";
            }
          }}
        >
          <div className="flex items-center space-x-4 ml-4">
            <h3 className="text-lg font-bold text-gray-800 tracking-wide">
              GRAPH DATA
            </h3>
            <button
              className="bg-gray-600 hover:bg-[#206ab2] text-white px-1.5 py-1 rounded text-xs font-bold transition-colors"
              onClick={async (e) => {
                e.stopPropagation();

                if (!OPENROUTER_API_KEY) {
                  setAiError("OpenRouter API key not configured");
                  return;
                }

                // Clear previous results before starting new analysis
                setAiInterpretation(null);
                setAiError(null);
                setLoadingAiInterpretation(true);

                try {
                  // Calculate cloud coverage statistics
                  const cloudStats = {
                    annualAvg: annualAvgCloudCover.toFixed(1),
                    maxCloudCover: maxCloudCover.toFixed(1),
                    minCloudCover: minCloudCover.toFixed(1),
                    cloudRange: cloudRange.toFixed(1),
                    solarPotential: solarPotential.toFixed(1),
                    // Calculate seasonal averages
                    winterAvg: cloudCoverData.data
                      ? (() => {
                          const winterData = cloudCoverData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (cloudCoverData.data.length / 12)
                                  ) + 1;
                              return month === 12 || month === 1 || month === 2;
                            }
                          );
                          return winterData.length > 0
                            ? (
                                winterData.reduce((sum, d) => sum + d.mean, 0) /
                                winterData.length
                              ).toFixed(1)
                            : "0";
                        })()
                      : "0",
                    springAvg: cloudCoverData.data
                      ? (() => {
                          const springData = cloudCoverData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (cloudCoverData.data.length / 12)
                                  ) + 1;
                              return month === 3 || month === 4 || month === 5;
                            }
                          );
                          return springData.length > 0
                            ? (
                                springData.reduce((sum, d) => sum + d.mean, 0) /
                                springData.length
                              ).toFixed(1)
                            : "0";
                        })()
                      : "0",
                    summerAvg: cloudCoverData.data
                      ? (() => {
                          const summerData = cloudCoverData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (cloudCoverData.data.length / 12)
                                  ) + 1;
                              return month === 6 || month === 7 || month === 8;
                            }
                          );
                          return summerData.length > 0
                            ? (
                                summerData.reduce((sum, d) => sum + d.mean, 0) /
                                summerData.length
                              ).toFixed(1)
                            : "0";
                        })()
                      : "0",
                    fallAvg: cloudCoverData.data
                      ? (() => {
                          const fallData = cloudCoverData.data.filter(
                            (item, index) => {
                              const month = item.month
                                ? item.month
                                : Math.floor(
                                    index / (cloudCoverData.data.length / 12)
                                  ) + 1;
                              return (
                                month === 9 || month === 10 || month === 11
                              );
                            }
                          );
                          return fallData.length > 0
                            ? (
                                fallData.reduce((sum, d) => sum + d.mean, 0) /
                                fallData.length
                              ).toFixed(1)
                            : "0";
                        })()
                      : "0",
                  };

                  const response = await fetch(
                    "https://openrouter.ai/api/v1/chat/completions",
                    {
                      method: "POST",
                      headers: {
                        Authorization: `Bearer ${OPENROUTER_API_KEY}`,
                        "HTTP-Referer": window.location.origin,
                        "X-Title": "ClimaPro.AI",
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({
                        model: "anthropic/claude-3.7-sonnet",
                        route: "fallback",
                        messages: [
                          {
                            role: "system",
                            content:
                              "You are an expert climate data analyst specializing in cloud coverage and solar potential graph interpretation. Focus exclusively on analyzing cloud coverage patterns, solar potential trends, seasonal variations, and their relationship. Provide insights specifically about what the cloud coverage and solar potential graphs reveal, not general climate advice. Keep responses concise, 100 words, focusing on graph-specific observations.",
                          },
                          {
                            role: "user",
                            content: `Analyze the cloud coverage and solar potential graph patterns for this ${
                              climateInfo.mainClass
                            }${climateInfo.subClass} (${
                              climateInfo.fullName
                            }) climate location:

ANNUAL STATISTICS:
- Annual Average Cloud Coverage: ${cloudStats.annualAvg}%
- Solar Potential: ${cloudStats.solarPotential}%
- Maximum Cloud Coverage: ${cloudStats.maxCloudCover}%
- Minimum Cloud Coverage: ${cloudStats.minCloudCover}%
- Total Cloud Range: ${cloudStats.cloudRange}%

SEASONAL PATTERNS:
- Winter (Dec-Feb): ${cloudStats.winterAvg}% cloud coverage
- Spring (Mar-May): ${cloudStats.springAvg}% cloud coverage
- Summer (Jun-Aug): ${cloudStats.summerAvg}% cloud coverage
- Fall (Sep-Nov): ${cloudStats.fallAvg}% cloud coverage

SEASONAL CLOUD RANGES:
- Winter-Summer difference: ${(
                              parseFloat(cloudStats.summerAvg) -
                              parseFloat(cloudStats.winterAvg)
                            ).toFixed(1)}%
- Spring-Fall difference: ${(
                              parseFloat(cloudStats.springAvg) -
                              parseFloat(cloudStats.fallAvg)
                            ).toFixed(1)}%

Analyze specifically what these cloud coverage and solar potential graphs reveal about:
1. Seasonal cloud transition patterns and solar access timing
2. Cloud coverage variability and solar potential within this ${
                              climateInfo.mainClass
                            }${climateInfo.subClass} classification
3. Peak and minimum cloudiness patterns visible in the graph
4. Inverse relationship between cloud coverage and solar potential
5. Notable cloud coverage extremes or solar access patterns shown in the data

Focus only on what the cloud coverage and solar potential graph data and patterns show, not design recommendations.`,
                          },
                        ],
                      }),
                    }
                  );

                  if (!response.ok) {
                    if (response.status === 503) {
                      throw new Error("AI service temporarily unavailable");
                    } else if (response.status === 401) {
                      throw new Error("Authentication failed");
                    } else if (response.status === 429) {
                      throw new Error("Rate limit exceeded");
                    }
                    throw new Error(`Request failed: ${response.status}`);
                  }

                  const data = await response.json();

                  if (
                    data.choices &&
                    data.choices[0] &&
                    data.choices[0].message
                  ) {
                    setAiInterpretation(data.choices[0].message.content);
                  } else if (data.error) {
                    throw new Error(data.error.message || "Unknown AI error");
                  }
                } catch (error) {
                  console.error(
                    "AI cloud coverage interpretation error:",
                    error
                  );
                  setAiError(error.message);
                } finally {
                  setLoadingAiInterpretation(false);
                }
              }}
              disabled={loadingAiInterpretation}
            >
              {loadingAiInterpretation ? "..." : "ClimaPro.AI"}
            </button>
          </div>
          <svg
            id="cloud-graph-chevron"
            className="w-8 h-8 text-gray-600 transition-transform duration-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        <div
          id="cloud-graph-content"
          style={{ display: "none" }}
          className="px-8 pb-8"
        >
          {cloudCoverData && cloudCoverData.data && (
            <>
              {/* Conditional Layout: Split when AI content exists, full width otherwise */}
              {aiInterpretation || aiError || loadingAiInterpretation ? (
                <div className="flex gap-6">
                  {/* AI Interpretation Section - Left 30% */}
                  <div className="w-[30%] bg-gray-100">
                    <div className="h-full">
                      <h4 className="font-bold text-gray-800 mb-4">
                        AI Cloud Coverage Analysis
                      </h4>
                      {loadingAiInterpretation && (
                        <div className="space-y-3">
                          {/* Skeleton loading rectangles with color animation */}
                          <style jsx>{`
                            @keyframes cloudColorWave1 {
                              0%,
                              100% {
                                background-color: #d1d5db;
                              }
                              50% {
                                background-color: #9ca3af;
                              }
                            }
                            @keyframes cloudColorWave2 {
                              0%,
                              100% {
                                background-color: #9ca3af;
                              }
                              50% {
                                background-color: #d1d5db;
                              }
                            }
                            @keyframes cloudColorWave3 {
                              0%,
                              100% {
                                background-color: #e5e7eb;
                              }
                              50% {
                                background-color: #9ca3af;
                              }
                            }
                            .animate-cloud-wave-1 {
                              animation: cloudColorWave1 2s ease-in-out infinite;
                            }
                            .animate-cloud-wave-2 {
                              animation: cloudColorWave2 2s ease-in-out infinite
                                0.3s;
                            }
                            .animate-cloud-wave-3 {
                              animation: cloudColorWave3 2s ease-in-out infinite
                                0.6s;
                            }
                          `}</style>
                          <div className="h-3 rounded animate-cloud-wave-1"></div>
                          <div className="h-3 rounded animate-cloud-wave-2 w-4/5"></div>
                          <div className="h-3 rounded animate-cloud-wave-3 w-3/5"></div>
                          <div className="h-3 rounded animate-cloud-wave-1 w-5/6"></div>
                          <div className="h-3 rounded animate-cloud-wave-2 w-2/3"></div>
                          <div className="h-3 rounded animate-cloud-wave-3"></div>
                          <div className="h-3 rounded animate-cloud-wave-1 w-3/4"></div>
                          <div className="h-3 rounded animate-cloud-wave-2 w-4/5"></div>
                          <div className="h-3 rounded animate-cloud-wave-3 w-1/2"></div>
                          <div className="h-3 rounded animate-cloud-wave-1 w-3/5"></div>
                          <div className="h-3 rounded animate-cloud-wave-2 w-5/6"></div>
                          <div className="h-3 rounded animate-cloud-wave-3 w-2/3"></div>
                        </div>
                      )}
                      {aiError && (
                        <div className="text-red-600 text-sm font-bold">
                          Error: {aiError}
                        </div>
                      )}
                      {aiInterpretation && (
                        <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap font-bold">
                          {aiInterpretation}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Graph Section - Right 70% when AI content exists */}
                  <div className="w-[70%] h-96 bg-gray-100 rounded-2xl p-6 flex flex-col justify-center">
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={(() => {
                            // Create weekly averages instead of daily data
                            const weeklyData = [];
                            const dataLength = cloudCoverData.data.length;
                            const weekSize = 7; // 7 days per week

                            for (let i = 0; i < dataLength; i += weekSize) {
                              const weekData = cloudCoverData.data.slice(
                                i,
                                i + weekSize
                              );
                              if (weekData.length > 0) {
                                const avgCloudCover =
                                  weekData.reduce((sum, d) => sum + d.mean, 0) /
                                  weekData.length;
                                const weekIndex = Math.floor(i / weekSize);
                                const monthIndex = Math.floor(
                                  (i / dataLength) * 12
                                );

                                weeklyData.push({
                                  month:
                                    [
                                      "Jan",
                                      "Feb",
                                      "Mar",
                                      "Apr",
                                      "May",
                                      "Jun",
                                      "Jul",
                                      "Aug",
                                      "Sep",
                                      "Oct",
                                      "Nov",
                                      "Dec",
                                    ][monthIndex] || "Jan",
                                  cloudCover: avgCloudCover,
                                  solarPotential: 100 - avgCloudCover,
                                  dayOfYear: weekData[0].day_of_year || i,
                                  originalIndex: weekIndex,
                                  weekIndex: weekIndex,
                                });
                              }
                            }
                            return weeklyData;
                          })()}
                          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#f0f0f0"
                          />
                          <XAxis
                            dataKey="month"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: "#666" }}
                            interval={Math.floor(52 / 12)}
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: "#666" }}
                            label={{
                              value: "%",
                              angle: -90,
                              position: "insideLeft",
                            }}
                            domain={[0, 100]}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "white",
                              border: "1px solid #ccc",
                              borderRadius: "8px",
                            }}
                            formatter={(value, name) => [
                              `${value.toFixed(1)}%`,
                              name === "cloudCover"
                                ? "Cloud Coverage"
                                : "Solar Potential",
                            ]}
                          />

                          <ReferenceLine
                            y={30}
                            stroke="#e0e0e0"
                            strokeDasharray="2 2"
                          />
                          <ReferenceLine
                            y={70}
                            stroke="#e0e0e0"
                            strokeDasharray="2 2"
                          />

                          <Line
                            type="monotone"
                            dataKey="cloudCover"
                            stroke="#6b7280"
                            strokeWidth={2}
                            dot={false}
                            name="cloudCover"
                          />
                          <Line
                            type="monotone"
                            dataKey="solarPotential"
                            stroke="#f59e0b"
                            strokeWidth={2}
                            dot={false}
                            name="solarPotential"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="flex justify-center space-x-8 mt-4 text-sm">
                      <div className="flex items-center">
                        <div className="w-6 h-0.5 bg-gray-600 mr-3"></div>
                        <span className="text-gray-700">Cloud Coverage</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-6 h-0.5 bg-amber-500 mr-3"></div>
                        <span className="text-gray-700">Solar Potential</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-6 h-0.5 bg-gray-400 mr-3 border-dashed border-t"></div>
                        <span className="text-gray-700">
                          Clear/Overcast Thresholds
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Full Width Graph - When no AI content */
                <div className="h-96 bg-gray-100 rounded-2xl p-6 flex flex-col justify-center">
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={(() => {
                          // Create weekly averages instead of daily data
                          const weeklyData = [];
                          const dataLength = cloudCoverData.data.length;
                          const weekSize = 7; // 7 days per week

                          for (let i = 0; i < dataLength; i += weekSize) {
                            const weekData = cloudCoverData.data.slice(
                              i,
                              i + weekSize
                            );
                            if (weekData.length > 0) {
                              const avgCloudCover =
                                weekData.reduce((sum, d) => sum + d.mean, 0) /
                                weekData.length;
                              const weekIndex = Math.floor(i / weekSize);
                              const monthIndex = Math.floor(
                                (i / dataLength) * 12
                              );

                              weeklyData.push({
                                month:
                                  [
                                    "Jan",
                                    "Feb",
                                    "Mar",
                                    "Apr",
                                    "May",
                                    "Jun",
                                    "Jul",
                                    "Aug",
                                    "Sep",
                                    "Oct",
                                    "Nov",
                                    "Dec",
                                  ][monthIndex] || "Jan",
                                cloudCover: avgCloudCover,
                                solarPotential: 100 - avgCloudCover,
                                dayOfYear: weekData[0].day_of_year || i,
                                originalIndex: weekIndex,
                                weekIndex: weekIndex,
                              });
                            }
                          }
                          return weeklyData;
                        })()}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="month"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: "#666" }}
                          interval={Math.floor(52 / 12)}
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: "#666" }}
                          label={{
                            value: "%",
                            angle: -90,
                            position: "insideLeft",
                          }}
                          domain={[0, 100]}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "white",
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                          }}
                          formatter={(value, name) => [
                            `${value.toFixed(1)}%`,
                            name === "cloudCover"
                              ? "Cloud Coverage"
                              : "Solar Potential",
                          ]}
                        />

                        <ReferenceLine
                          y={30}
                          stroke="#e0e0e0"
                          strokeDasharray="2 2"
                        />
                        <ReferenceLine
                          y={70}
                          stroke="#e0e0e0"
                          strokeDasharray="2 2"
                        />

                        <Line
                          type="monotone"
                          dataKey="cloudCover"
                          stroke="#6b7280"
                          strokeWidth={2}
                          dot={false}
                          name="cloudCover"
                        />
                        <Line
                          type="monotone"
                          dataKey="solarPotential"
                          stroke="#f59e0b"
                          strokeWidth={2}
                          dot={false}
                          name="solarPotential"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="flex justify-center space-x-8 mt-4 text-sm">
                    <div className="flex items-center">
                      <div className="w-6 h-0.5 bg-gray-600 mr-3"></div>
                      <span className="text-gray-700">Cloud Coverage</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-6 h-0.5 bg-amber-500 mr-3"></div>
                      <span className="text-gray-700">Solar Potential</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-6 h-0.5 bg-gray-400 mr-3 border-dashed border-t"></div>
                      <span className="text-gray-700">
                        Clear/Overcast Thresholds
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CloudCoverageAnalysis;
